import type { ComplaintArticleData } from '@/components/dealer/ComplaintArticleRow';
import type { AddressType, Complaint, ComplaintItem } from '@/types/service-api';

/**
 * Form data interface matching ComplaintForm component
 */
export interface ComplaintFormData {
  // Auftragskopfdaten
  orderNr: number | string;
  orderSubNr: number | string;
  commission: string;
  description: string;

  // Fachhändler
  dealerNumber: string;
  dealerName: string;
  dealerStreet: string;
  dealerCountry: string;
  dealerPostalCode: string;
  dealerCity: string;
  dealerContactName: string;
  dealerContactPhone: string;
  dealerContactMobile: string;
  dealerContactEmail: string;

  // Lieferadresse
  deliveryCompletionLocation: string;
  deliveryName: string;
  deliveryStreet: string;
  deliveryCountry: string;
  deliveryPostalCode: string;
  deliveryCity: string;
  deliveryContactName: string;
  deliveryContactPhone: string;
  deliveryContactLocation: string;
}

/**
 * Maps friendly completion location values to API AddressType codes
 */
const ADDRESS_TYPE_MAP: Record<string, AddressType> = {
  dealer: 'FH', // Fachhändler
  customer: 'EK', // Endkunde
  warehouse: 'L', // Lager
  unclear: 'U', // Ungeklärt
};

/**
 * Transforms flat form data structure into nested API Complaint structure
 *
 * @param formData - Data collected from ComplaintForm component
 * @param articles - Selected articles from ComplaintArticlesList
 * @returns Complaint object ready for API submission
 *
 * @example
 * const complaint = transformFormDataToComplaint(formData, selectedArticles);
 * await createComplaint(complaint);
 */
export function transformFormDataToComplaint(
  formData: ComplaintFormData,
  articles: ComplaintArticleData[]
): Complaint {
  // Convert completion location to AddressType code
  const addressType = ADDRESS_TYPE_MAP[formData.deliveryCompletionLocation] || 'U';

  // Filter to only include selected articles
  const selectedArticles = articles.filter((article) => article.isSelected);

  // Transform articles to ComplaintItem format
  const complaintItems: ComplaintItem[] = selectedArticles.map((article) => ({
    itemNr: article.position,
    complainedAmount: Number(article.quantity) || 0,
    model: article.model || undefined,
    articleNr: article.articleNumber || undefined,
    variant1: article.variant1 || undefined,
    variant2: article.variant2 || undefined,
    variant3: article.variant3 || undefined,
    variant4: article.variant4 || undefined,
    variant5: article.variant5 || undefined,
    description: article.articleDescription || undefined,
    faultDescription: article.errorDescription,
    discountRequested: article.priceReductionWished,
    customerServiceRequested: article.completedByCustomer,
  }));

  // Build the complaint object
  const complaint: Complaint = {
    header: {
      orderNr: Number(formData.orderNr),
      orderSubNr: Number(formData.orderSubNr),
      commission: formData.commission,
      comment: formData.description || undefined,
      customer: {
        customerNr: Number(formData.dealerNumber),
        name: formData.dealerName,
        street: formData.dealerStreet,
        country: formData.dealerCountry,
        postCode: formData.dealerPostalCode,
        city: formData.dealerCity,
        contactPerson: {
          name: formData.dealerContactName,
          telNr: formData.dealerContactPhone || undefined,
          mobileNr: formData.dealerContactMobile || undefined,
          mail: formData.dealerContactEmail || undefined,
        },
      },
      shippingAddress: {
        type: addressType,
        name: formData.deliveryName,
        street: formData.deliveryStreet,
        country: formData.deliveryCountry,
        postCode: formData.deliveryPostalCode,
        city: formData.deliveryCity,
        // Include contactPerson - empty object for non-customer types (Backend expects this field)
        contactPerson:
          formData.deliveryCompletionLocation === 'customer'
            ? {
                name: formData.deliveryContactName,
                phoneNumber: formData.deliveryContactPhone,
                location: formData.deliveryContactLocation,
              }
            : {},
      },
    },
    complaintItems,
  };

  return complaint;
}
